import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { mismaPersona } from '../utils/nameMatch';
import { Plus, Search, Sparkles, ArrowUpDown, ArrowUp, ArrowDown, Trash2 } from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';
import PolicyModal from '../components/policies/PolicyModal';
import PdfUpload, { ExcelUpload } from '../components/policies/PdfUpload';
import { findSimilarClients, normalize } from './ClientsPage';

const STATUS_LABEL = { active: 'Vigente', endorsed: 'Endosada', cancelled: 'Cancelada', expired: 'Vencida', future: 'Futura' };
const STATUS_CLASS = { active: 'badge-green', endorsed: 'badge-blue', cancelled: 'badge-red', expired: 'badge-amber', future: 'badge-blue' };
const TYPE_ICONS = {
  auto:'🚗', moto:'🏍️', truck:'🚛', fleet:'🚌', boat:'⛵', tractor:'🚜',
  home:'🏠', commercial:'🏢', building:'🏗️', agro:'🌾', construction:'🔨', glass:'🪟',
  life:'❤️', life_term:'💙', accident:'🩺', health:'💊', disability:'♿', funeral:'🌹',
  liability:'⚖️', liability_professional:'📋', liability_products:'📦',
  technical:'⚙️', cargo:'🚢', fire:'🔥', fidelity:'🤝', surety:'📜',
  cyber:'💻', directors:'👔', workers:'👷',
  travel:'✈️', pet:'🐾', credit:'💳', art:'🖼️', other:'📄'
};
const TYPE_LABELS = {
  auto:'Automóvil', moto:'Motocicleta', truck:'Transporte/Camión', fleet:'Flota vehicular', boat:'Embarcación', tractor:'Tractor/Maquinaria',
  home:'Hogar/Apartamento', commercial:'Comercio/Local', building:'Edificio', agro:'Agro/Rural', construction:'Construcción', glass:'Vidrios',
  life:'Vida', life_term:'Vida término', accident:'Accidentes personales', health:'Salud', disability:'Incapacidad', funeral:'Sepelio',
  liability:'Resp. civil', liability_professional:'RC Profesional', liability_products:'RC Productos',
  technical:'Equipos/Electrónico', cargo:'Trans. mercadería', fire:'Incendio industrial', fidelity:'Fidelidad', surety:'Caución',
  cyber:'Riesgo cibernético', directors:'D&O', workers:'Acc. trabajo',
  travel:'Asistencia viajero', pet:'Mascotas', credit:'Crédito', art:'Obras de arte', other:'Otro'
};

function SortIcon({ field, sortBy, sortDir }) {
  if (sortBy !== field) return <ArrowUpDown size={11} style={{ color: 'var(--text-muted)', marginLeft: 3 }} />;
  return sortDir === 'asc' ? <ArrowUp size={11} style={{ color: 'var(--accent)', marginLeft: 3 }} /> : <ArrowDown size={11} style={{ color: 'var(--accent)', marginLeft: 3 }} />;
}

// ── Modal de resultado de carga masiva ─────────────────────
// Detalle fila por fila de la última importación: qué entró, qué se omitió
// y por qué. Marca en rojo los duplicados cuyo nombre extraído NO coincide
// con el cliente dueño de la póliza existente (posible carga mal asignada)
// y permite reasignarla, o reintentar los registros con error.
const IMPORT_STATUS = {
  created:     { label: 'Creada',            class: 'badge-green' },
  duplicate:   { label: 'Duplicado',         class: 'badge-gray'  },
  client_only: { label: 'Solo cliente',      class: 'badge-blue'  },
  skipped:     { label: 'Omitida',           class: 'badge-amber' },
  error:       { label: 'Error',             class: 'badge-red'   },
};

function ImportResultModal({ resultados, onClose, onReasignar, onReintentar, onVerPoliza }) {
  const counts = resultados.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});
  const mismatches = resultados.filter(r => r.mismatch).length;
  const revisar = resultados.filter(r => r.needsReview).length;
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 860 }}>
        <div className="modal-header">
          <h2 style={{ fontSize: 16 }}>Resultado de la carga</h2>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
            {Object.entries(IMPORT_STATUS).map(([k, s]) => counts[k] ? (
              <span key={k} className={`badge ${s.class}`}>{counts[k]} {s.label.toLowerCase()}{counts[k] !== 1 ? 's' : ''}</span>
            ) : null)}
            {mismatches > 0 && <span className="badge badge-red">⚠ {mismatches} posible{mismatches !== 1 ? 's' : ''} mal asignada{mismatches !== 1 ? 's' : ''}</span>}
            {revisar > 0 && <span className="badge badge-amber">⚠ {revisar} con fecha a revisar</span>}
          </div>
          {mismatches > 0 && (
            <div style={{ fontSize: 12, padding: '10px 12px', background: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.3)', borderRadius: 8, color: 'var(--text-secondary)' }}>
              Hay pólizas que ya existen en el sistema pero a nombre de un cliente distinto al que figura en el documento.
              Revisalas: pueden ser cargas anteriores mal asignadas. "Reasignar" cuelga la póliza existente del cliente del documento.
            </div>
          )}
          <div style={{ overflowX: 'auto', maxHeight: '55vh', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: 11 }}>
                  <th style={{ padding: '6px 10px' }}>Cliente (documento)</th>
                  <th style={{ padding: '6px 10px' }}>Póliza</th>
                  <th style={{ padding: '6px 10px' }}>Compañía</th>
                  <th style={{ padding: '6px 10px' }}>Estado</th>
                  <th style={{ padding: '6px 10px' }}>Detalle</th>
                  <th style={{ padding: '6px 10px' }}></th>
                </tr>
              </thead>
              <tbody>
                {resultados.map((r, i) => {
                  const st = IMPORT_STATUS[r.status] || IMPORT_STATUS.skipped;
                  return (
                    <tr key={i} style={{ borderTop: '1px solid var(--border)', background: r.mismatch ? 'rgba(255,80,80,0.05)' : r.needsReview ? 'rgba(245,158,11,0.06)' : 'transparent' }}>
                      <td style={{ padding: '8px 10px', fontWeight: 500 }}>{r.clientName || '—'}</td>
                      <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{r.policyNumber || '—'}</td>
                      <td style={{ padding: '8px 10px' }}>{r.company || '—'}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <span className={`badge ${r.needsReview ? 'badge-amber' : st.class}`} style={{ fontSize: 10 }}>{r.mismatch ? '⚠ Mal asignada?' : r.needsReview ? '⚠ Revisar fecha' : st.label}</span>
                      </td>
                      <td style={{ padding: '8px 10px', color: r.mismatch ? 'var(--red)' : r.needsReview ? 'var(--amber)' : 'var(--text-secondary)', maxWidth: 280 }}>{r.detail}</td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                        {r.mismatch && <button className="btn btn-danger btn-sm" style={{ marginRight: 6 }} onClick={() => onReasignar(i)}>Reasignar</button>}
                        {r.status === 'error' && <button className="btn btn-secondary btn-sm" style={{ marginRight: 6 }} onClick={() => onReintentar(i)}>Reintentar</button>}
                        {r.existingPolicyId && <button className="btn btn-secondary btn-sm" onClick={() => onVerPoliza(r.existingPolicyId)}>Ver</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="modal-footer">
          <span style={{ fontSize: 11, color: 'var(--text-muted)', marginRight: 'auto' }}>Este detalle queda guardado en Revisión de cargas.</span>
          <button className="btn btn-primary" onClick={onClose}>Entendido</button>
        </div>
      </div>
    </div>
  );
}

export default function PoliciesPage() {
  const { policies, clients, addClient, addPolicy, updatePolicy, deletePolicy, showToast } = useApp();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('endDate');
  const [sortDir, setSortDir] = useState('asc');
  const [modal, setModal] = useState(false);
  const [pdfModal, setPdfModal] = useState(false);
  const [excelModal, setExcelModal] = useState(false);
  const [importResult, setImportResult] = useState(null); // detalle de la última carga

  const getClient = id => clients.find(c => c.id === id) || {};

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('asc'); }
  };

  const filtered = useMemo(() => {
    let list = policies.filter(p => {
      const c = getClient(p.clientId);
      const q = normalize(search);
      const matchSearch = !search ||
        normalize(c.name).includes(q) ||
        normalize(p.number).includes(q) ||
        normalize(p.company).includes(q) ||
        normalize(p.assetData?.plate).includes(q) ||
        normalize(TYPE_LABELS[p.type] || '').includes(q);
      const matchStatus = filterStatus === 'all' || p.status === filterStatus;
      const matchType = filterType === 'all' || p.type === filterType;
      return matchSearch && matchStatus && matchType;
    });
    return [...list].sort((a, b) => {
      let vA, vB;
      const cA = getClient(a.clientId); const cB = getClient(b.clientId);
      switch (sortBy) {
        case 'endDate':   vA = a.endDate || ''; vB = b.endDate || ''; break;
        case 'startDate': vA = a.startDate || ''; vB = b.startDate || ''; break;
        case 'client':    vA = cA.name?.toLowerCase() || ''; vB = cB.name?.toLowerCase() || ''; break;
        case 'company':   vA = a.company?.toLowerCase() || ''; vB = b.company?.toLowerCase() || ''; break;
        case 'premium':   vA = Number(a.premium) || 0; vB = Number(b.premium) || 0; break;
        default:          vA = a.number || ''; vB = b.number || '';
      }
      if (vA < vB) return sortDir === 'asc' ? -1 : 1;
      if (vA > vB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [policies, clients, search, filterStatus, filterType, sortBy, sortDir]);

  const today = new Date();

  // Duplicate detection — usa similitud fonética además de coincidencia exacta
  // Email/teléfono del corredor (NEBB): aparecen en los avisos de Porto como
  // "domicilio electrónico" y la IA a veces los extrae como datos del cliente.
  // Si se usan para identificar, TODAS las pólizas de avisos Porto terminan
  // colgadas del primer cliente que se importó con ese email. Nunca identifican.
  const esContactoCorredor = (v) => {
    const s = (v || '').toLowerCase().replace(/[\s.\-()]/g, '');
    return s.includes('nebbrokers') || s.includes('29155202');
  };

  const findExistingClient = (data) => {
    if (!data.clientName) return null;
    const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[\s,.]+/g, ' ').trim();
    // ¿Los nombres son compatibles? (comparten al menos un apellido/palabra de 3+ letras)
    const nombresCompatibles = (a, b) => mismaPersona(a, b);
    // Primero buscar por CI/RUT exacto, PERO solo si el nombre es compatible.
    // Un mismo RUT cargado en el cliente equivocado (ej. quedó en NEBB de una
    // carga vieja) no debe arrastrar la póliza a ese cliente: el nombre manda.
    const byDni = data.clientDni && clients.find(c =>
      c.dni?.replace(/[\s.]/g,'') === data.clientDni?.replace(/[\s.]/g,'') &&
      nombresCompatibles(c.name, data.clientName)
    );
    if (byDni) return byDni;
    // Email/teléfono: solo si NO son del corredor y el nombre no contradice
    // (familias comparten email/teléfono, pero un nombre totalmente distinto
    // con mismo email es señal de dato sucio, no de mismo cliente).
    const byContact = clients.find(c => {
      const matchEmail = data.clientEmail && !esContactoCorredor(data.clientEmail) && c.email?.toLowerCase() === data.clientEmail?.toLowerCase();
      const matchPhone = data.clientPhone && !esContactoCorredor(data.clientPhone) && c.phone?.replace(/\s/g,'') === data.clientPhone?.replace(/\s/g,'');
      return (matchEmail || matchPhone) && nombresCompatibles(c.name, data.clientName);
    });
    if (byContact) return byContact;
    // Por nombre: SOLO coincidencia exacta (sin mayúsculas/tildes/espacios extra).
    // La similitud difusa (ej. 94%) asignaba pólizas al cliente equivocado con
    // apellidos parecidos. Si el nombre no es idéntico, se crea cliente nuevo:
    // un duplicado se detecta y une después; una póliza mal colgada es peor.
    const exact = clients.find(c => norm(c.name) === norm(data.clientName));
    return exact || null;
  };
  const findExistingPolicy = (data) => data.policyNumber ? policies.find(p => p.number?.trim().toLowerCase() === data.policyNumber?.trim().toLowerCase()) : null;

  const handleBatchImport = async (records, source = 'pdf-ia') => {
    const lista = Array.isArray(records) ? records : (records ? [records] : []);
    const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[\s,.]+/g, ' ').trim();
    const nombresCompatibles = (a, b) => mismaPersona(a, b);
    const resultados = [];
    for (const data of lista) {
      const item = {
        clientName: data.clientName || '', policyNumber: data.policyNumber || '',
        company: data.company || '', endDate: data.endDate || '', premium: data.premium || 0,
        status: '', detail: '', existingClientName: '', existingPolicyId: '', mismatch: false, needsReview: false,
        data, // datos extraídos completos, por si hay que reintentar/reasignar
      };
      try {
        const dupe = findExistingPolicy(data);
        if (dupe) {
          const dueño = clients.find(c => c.id === dupe.clientId);
          item.status = 'duplicate';
          item.existingPolicyId = dupe.id;
          item.existingClientName = dueño?.name || '(sin cliente)';
          // La póliza ya existe pero a nombre de OTRO: posible carga mal asignada
          if (data.clientName && dueño && !nombresCompatibles(dueño.name, data.clientName)) {
            item.mismatch = true;
            item.detail = `La póliza ya existe a nombre de "${dueño.name}" pero el documento dice "${data.clientName}"`;
          } else {
            item.detail = `Ya cargada${dueño ? ` (${dueño.name})` : ''}`;
          }
          resultados.push(item); continue;
        }
        let clientId;
        const existing = findExistingClient(data);
        if (existing) { clientId = existing.id; }
        else if (data.clientName) {
          const email = esContactoCorredor(data.clientEmail) ? '' : data.clientEmail;
          const phone = esContactoCorredor(data.clientPhone) ? '' : data.clientPhone;
          const nc = await addClient({ name: data.clientName, dni: data.clientDni, phone, email, address: data.clientAddress, notes: data.clientNotes || '' });
          clientId = nc.id;
        }
        if (!clientId) { item.status = 'skipped'; item.detail = 'Sin nombre de cliente en el documento'; resultados.push(item); continue; }
        if (!data.policyNumber && !data.company) { item.status = 'client_only'; item.detail = 'Solo datos de cliente, sin póliza'; resultados.push(item); continue; }

        // ── Fechas de la póliza ───────────────────────────────────────────
        // La extracción IA devuelve, para avisos de renovación:
        //  · renewalDateLogic: "future"   → el aviso trae la vigencia NUEVA
        //  · renewalDateLogic: "prev_end" → el aviso marca el FIN de la vigencia
        //    PASADA; la nueva va desde esa fecha + 1 año.
        //  · noticeDate: la fecha clave (inicio de vigencia nueva, o vencimiento
        //    de la pasada según el caso).
        //
        // En ambos casos la póliza entra VIGENTE y con fromRenewalNotice=true,
        // así cae en el módulo Renovaciones para que el equipo la trabaje con
        // "Renovar" (que recién ahí genera los 365 días definitivos).

        // Parseo seguro de YYYY-MM-DD sin corrimiento de zona horaria.
        const parseISODate = (s) => {
          if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
          const [y, m, d] = s.split('-').map(Number);
          if (!(m >= 1 && m <= 12) || !(d >= 1 && d <= 31)) return null;
          const dt = new Date(y, m - 1, d);
          return isNaN(dt) ? null : dt;
        };
        const addOneYear = (dt) => { const x = new Date(dt); x.setFullYear(x.getFullYear() + 1); return x; };

        let startDate, endDate, renovInfo = '';
        // Parseo previo de las fechas que vino del documento, para decidir el camino.
        const _ini = parseISODate(data.startDate);
        const _fin = parseISODate(data.endDate);
        // Si el documento trae una vigencia EXPLÍCITA y COMPLETA (inicio Y fin),
        // es una póliza o endoso emitido (no un aviso de renovación): hay que
        // respetar esas fechas tal cual, aunque la compañía sea de las que suelen
        // mandar avisos. Esto evita que pólizas/endosos de Porto, BSE, etc. caigan
        // en la lógica de renovación y se les recalcule mal la vigencia.
        // EXCEPCIÓN: si la IA marcó explícitamente isRenewalNotice, sí es un aviso
        // (ese camino igual respeta las fechas del PDF, solo lo manda a Renovaciones).
        const tieneVigenciaCompleta = !!(_ini && _fin);
        // Detección robusta de "aviso de renovación": no dependemos solo del flag
        // de la IA (que a veces no marca SBI/Barbuss). También cuenta si devolvió
        // una lógica de fecha de renovación, o si la compañía es de las que sus
        // PDFs SIEMPRE son avisos de renovación/vencimiento.
        const companiaAviso = ['sbi', 'barbuss', 'mapfre', 'bse', 'san cris', 'porto', 'sura', 'san crist']
          .some(k => (data.company || '').toLowerCase().includes(k));
        const tieneLogica = data.renewalDateLogic === 'future' || data.renewalDateLogic === 'prev_end';
        // Es aviso si: la IA lo marca explícitamente; O bien (cuando NO hay
        // vigencia completa en el documento) la lógica/compañía lo sugieren.
        const esAviso = !!data.isRenewalNotice || (!tieneVigenciaCompleta && (tieneLogica || companiaAviso));
        if (esAviso) {
          const hoy = new Date();
          const logic = data.renewalDateLogic || 'unknown';
          const notice = parseISODate(data.noticeDate);
          const inicioPDF = parseISODate(data.startDate);
          const finPDF = parseISODate(data.endDate);

          if (logic === 'future' && (inicioPDF || finPDF)) {
            // Grupo A: el aviso ya da la vigencia nueva. Usamos las fechas tal cual.
            const ini = inicioPDF || notice || hoy;
            const fin = finPDF || addOneYear(ini);
            startDate = format(ini, 'yyyy-MM-dd');
            endDate = format(fin, 'yyyy-MM-dd');
            renovInfo = ` · aviso de renovación (vigencia nueva ${startDate} → ${endDate}) (renovar)`;
          } else if (logic === 'future' && notice) {
            // Grupo A sin fin explícito: inicio = noticeDate, fin = +1 año.
            startDate = format(notice, 'yyyy-MM-dd');
            endDate = format(addOneYear(notice), 'yyyy-MM-dd');
            renovInfo = ` · aviso de renovación (vigencia nueva → ${endDate}) (renovar)`;
          } else if (logic === 'prev_end' && notice) {
            // Grupo B: noticeDate es el fin de la vigencia pasada. La nueva va
            // desde esa fecha (en adelante) + 1 año.
            startDate = format(notice, 'yyyy-MM-dd');
            endDate = format(addOneYear(notice), 'yyyy-MM-dd');
            renovInfo = ` · aviso (fin vigencia pasada ${startDate}) → renueva hasta ${endDate} (renovar)`;
          } else {
            // Fallback (sin fechas confiables): usar el mes de renovación sobre el
            // año corriente, como antes, para no perder el registro.
            const anioActual = hoy.getFullYear();
            const mesActual = hoy.getMonth() + 1;
            let mes = parseInt(data.renewalMonth, 10);
            if (!(mes >= 1 && mes <= 12)) mes = mesActual;
            const finMes = new Date(anioActual, mes, 0);
            endDate = format(finMes, 'yyyy-MM-dd');
            const ini = new Date(finMes); ini.setFullYear(ini.getFullYear() - 1); ini.setDate(ini.getDate() + 1);
            startDate = format(ini, 'yyyy-MM-dd');
            item.needsReview = true;
            renovInfo = ` · ⚠ no se pudo leer la fecha del aviso — estimada (vence ${endDate}), revisá`;
          }

          // Señal de revisión: si el vencimiento calculado quedó en el pasado o
          // muy cerca (dentro de los próximos 60 días contando desde la carga),
          // suele ser una póliza de la migración con vigencia pasada. Igual se
          // genera y entra a Renovaciones, pero la marcamos para que revisen.
          const finCalc = parseISODate(endDate);
          if (finCalc) {
            const limite = new Date(hoy); limite.setDate(limite.getDate() + 60);
            if (finCalc < hoy) {
              item.needsReview = true;
              renovInfo += ` · ⚠ vencimiento ${endDate} ya pasó — verificá el año`;
            } else if (finCalc <= limite && !item.needsReview) {
              // próximo: solo informativo, no bloquea
              renovInfo += ` · vence pronto (${endDate})`;
            }
          }
        } else {
          startDate = data.startDate || format(new Date(), 'yyyy-MM-dd');
          const d = parseISODate(startDate) || new Date(); d.setFullYear(d.getFullYear() + 1);
          endDate = data.endDate || format(d, 'yyyy-MM-dd');
        }

        const np = await addPolicy({
          clientId, number: data.policyNumber || 'POL-' + Date.now(),
          company: data.company || '', type: data.type || 'auto',
          coverage: data.coverage || '', premium: data.premium || 0,
          startDate, endDate,
          // Aviso de renovación SIEMPRE entra como vigente (nunca futura ni
          // vencida), para que sea renovable este mes.
          notes: data.notes || '', status: 'active',
          fromRenewalNotice: esAviso,
          deducible: data.deducible || '', cuotas: data.cuotas || '',
          assetData: {
            brand: data.assetBrand, model: data.assetModel, year: data.assetYear,
            plate: data.assetPlate, vin: data.assetVin, engine: data.assetEngine,
            padron: data.assetPadron, category: data.assetCategory, use: data.assetUse,
            zone: data.assetZone, fuel: data.assetFuel, color: data.assetColor,
            address: data.assetAddress, value: data.assetValue,
          }
        });
        item.status = 'created';
        item.existingPolicyId = np?.id || '';
        item.detail = (existing ? `Agregada a cliente existente (${existing.name})` : 'Cliente nuevo creado') + renovInfo;
        resultados.push(item);
      } catch (err) {
        item.status = 'error';
        item.detail = err.message || 'error desconocido';
        resultados.push(item);
        console.error('Error importando registro:', data?.clientName, err);
      }
    }
    // Registrar la carga en el servidor para revisión posterior (no bloquea la UI si falla)
    try {
      const token = localStorage.getItem('nebb_token');
      await fetch('/api/import-logs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ source, items: resultados.map(({ data, ...rest }) => rest) }),
      });
    } catch (e) { console.error('No se pudo registrar la carga:', e); }
    setImportResult(resultados);
  };

  // Reasigna una póliza existente al cliente que figura en el documento
  // (caso "posible mal asignada"): busca o crea el cliente correcto y
  // actualiza el clientId de la póliza. La sincronización de Renovaciones
  // refleja el cambio en la próxima corrida.
  const reasignarPoliza = async (idx) => {
    const item = importResult[idx];
    if (!item?.existingPolicyId || !item.data?.clientName) return;
    if (!window.confirm(`¿Reasignar la póliza ${item.policyNumber} de "${item.existingClientName}" a "${item.data.clientName}"?`)) return;
    try {
      let clientId;
      const existing = findExistingClient(item.data);
      if (existing) clientId = existing.id;
      else {
        const email = esContactoCorredor(item.data.clientEmail) ? '' : item.data.clientEmail;
        const phone = esContactoCorredor(item.data.clientPhone) ? '' : item.data.clientPhone;
        const nc = await addClient({ name: item.data.clientName, dni: item.data.clientDni, phone, email, address: item.data.clientAddress, notes: '' });
        clientId = nc.id;
      }
      await updatePolicy(item.existingPolicyId, { clientId });
      setImportResult(rs => rs.map((r, i) => i === idx ? { ...r, mismatch: false, status: 'created', detail: `Reasignada a ${item.data.clientName}` } : r));
      showToast(`✅ Póliza ${item.policyNumber} reasignada a ${item.data.clientName}`);
    } catch (err) {
      showToast('Error al reasignar: ' + err.message, 'error');
    }
  };

  // Reintenta la importación de un registro que falló
  const reintentarRegistro = async (idx) => {
    const item = importResult[idx];
    if (!item?.data) return;
    const previo = importResult;
    setImportResult(null);
    await handleBatchImport([item.data]);
    // El nuevo resultado reemplaza solo ese registro dentro del detalle previo
    setImportResult(rs => {
      const nuevo = rs && rs.length === 1 ? rs[0] : null;
      return nuevo ? previo.map((r, i) => i === idx ? nuevo : r) : previo;
    });
  };

  const handleDeletePolicy = async (e, p) => {
    e.stopPropagation();
    const c = getClient(p.clientId);
    if (!window.confirm(`¿Eliminar la póliza ${p.number}${c.name ? ` de ${c.name}` : ''}?\n\nEsta acción es irreversible. El cliente NO se elimina.`)) return;
    try { await deletePolicy(p.id); }
    catch (err) { showToast('Error al eliminar: ' + err.message, 'error'); }
  };

  const Th = ({ field, children }) => (
    <th onClick={() => handleSort(field)} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>{children}<SortIcon field={field} sortBy={sortBy} sortDir={sortDir} /></span>
    </th>
  );

  const usedTypes = [...new Set(policies.map(p => p.type).filter(Boolean))].sort();

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Pólizas</h1>
          <p className="page-subtitle">{filtered.length} de {policies.length} pólizas</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => setExcelModal(true)}><span>📊</span> Importar Excel</button>
          <button className="btn btn-secondary" onClick={() => setPdfModal(true)}><Sparkles size={14} /> PDF con IA</button>
          <button className="btn btn-primary" onClick={() => setModal(true)}><Plus size={15} /> Nueva póliza</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por cliente, N° póliza, matrícula, ramo..." style={{ paddingLeft: 36 }} />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ width: 160 }}>
          <option value="all">Todos los estados</option>
          <option value="active">Vigentes</option>
          <option value="future">Futuras 🔜</option>
          <option value="endorsed">Endosadas</option>
          <option value="expired">Vencidas ⚠️</option>
          <option value="cancelled">Canceladas</option>
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ width: 180 }}>
          <option value="all">Todos los ramos</option>
          {usedTypes.map(t => <option key={t} value={t}>{TYPE_ICONS[t] || '📄'} {TYPE_LABELS[t] || t}</option>)}
        </select>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-wrap policies-cards">
          <table>
            <thead>
              <tr>
                <Th field="client">Cliente</Th>
                <th>N° Póliza</th>
                <th>Ramo</th>
                <Th field="company">Compañía</Th>
                <th>Bien asegurado</th>
                <Th field="premium">Prima</Th>
                <Th field="endDate">Vencimiento</Th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const c = getClient(p.clientId);
                const asset = p.assetData || {};
                const isVehicle = ['auto','moto','truck','fleet','boat'].includes(p.type);
                const isProperty = ['home','commercial','building','agro'].includes(p.type);
                const assetStr = isVehicle ? [asset.brand, asset.model, asset.plate].filter(Boolean).join(' · ') : isProperty ? (asset.address || '') : '';
                const daysLeft = p.endDate ? differenceInDays(parseISO(p.endDate), today) : null;
                const isExpiringSoon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 30 && (p.status === 'active' || p.status === 'endorsed');
                return (
                  <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/policies/${p.id}`)}>
                    <td><div style={{ fontWeight: 500, fontSize: 13 }}>{c.name}</div>{c.phone && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.phone}</div>}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)' }}>{p.number}</td>
                    <td style={{ fontSize: 14 }} title={TYPE_LABELS[p.type]}>{TYPE_ICONS[p.type] || '📄'} <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{TYPE_LABELS[p.type] || p.type}</span></td>
                    <td>{p.company}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 160 }}><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{assetStr}</span></td>
                    <td style={{ fontFamily: 'var(--font-display)', fontWeight: 600 }}>{p.premium ? (p.currency === 'USD' ? 'U$S' : '$') + Number(p.premium).toLocaleString('es-UY') : '—'}</td>
                    <td style={{ fontSize: 12 }}>
                      <div>{p.endDate ? format(parseISO(p.endDate), 'dd/MM/yyyy') : '—'}</div>
                      {isExpiringSoon && <div style={{ fontSize: 10, color: 'var(--amber)', fontWeight: 600 }}>⚠️ {daysLeft}d</div>}
                    </td>
                    <td><span className={`badge ${STATUS_CLASS[p.status] || 'badge-gray'}`}>{STATUS_LABEL[p.status] || p.status}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); navigate(`/policies/${p.id}`); }}>Ver</button>
                        <button className="btn btn-danger btn-sm" title="Eliminar póliza" style={{ padding: '6px 8px' }} onClick={e => handleDeletePolicy(e, p)}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-muted)' }}><p>No se encontraron pólizas</p></div>}
      </div>

      {modal && <PolicyModal onClose={() => setModal(false)} />}
      {pdfModal && <PdfUpload existingClients={clients} existingPolicies={policies} onDataExtracted={records => handleBatchImport(records, 'pdf-ia')} onClose={() => setPdfModal(false)} />}
      {excelModal && <ExcelUpload existingClients={clients} existingPolicies={policies} onDataExtracted={records => handleBatchImport(records, 'excel')} onClose={() => setExcelModal(false)} />}
      {importResult && (
        <ImportResultModal
          resultados={importResult}
          onClose={() => setImportResult(null)}
          onReasignar={reasignarPoliza}
          onReintentar={reintentarRegistro}
          onVerPoliza={(id) => { setImportResult(null); navigate(`/policies/${id}`); }}
        />
      )}
    </div>
  );
}
